<template>
    <v-navigation-drawer
        id="navigation-drawer"
        v-model="drawer"
        app
        temporary
        disableResizeWatcher
    >
        <v-container>
            <v-row class="nav-heading">
                <v-col cols="8" class="nav-heading__column">
                    <img
                        class="logo"
                        src="@/assets/images/logo-white-horizontal.svg"
                    />
                </v-col>
                <v-col cols="4" class="nav-heading__close-button">
                    <v-btn
                        icon
                        large
                        color="white"
                        @click="drawer = false"
                    >
                        <v-icon>mdi-close-circle-outline</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-list class="nav-list" nav>
                    <v-list-item-group>
                        <div
                            v-for="item in permissibleLinkGroupList"
                            :key="item.title"
                            class="nav-list__list-item-wrapper"
                        >
                            <v-list-item
                                v-if="item.routeList.length === 1"
                                class="nav-list__nav-list-item"
                                :to="item.routeList[ 0 ]"
                                @click="drawer = false"
                            >
                                <v-list-item-content >
                                    <v-list-item-title v-text="item.title"/>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-group
                                v-else
                                class="nav-list__nav-list-group"
                                :value="currentRouteIsInList( item.routeList )"
                            >
                                <template v-slot:activator>
                                    <v-list-item-title v-text="item.title"/>
                                </template>
                                <v-list-item-group>
                                    <v-list-item
                                        v-for="route in item.routeList"
                                        :key="route.name"
                                        :to="route"
                                        @click="drawer = false"
                                    >
                                        <v-list-item-content class="nav-list__nav-list-item" >
                                            <v-list-item-title v-text="route.meta.title"/>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list-item-group>
                            </v-list-group>
                        </div>
                    </v-list-item-group>
                </v-list>
            </v-row>
        </v-container>
        <template #append>
            <v-row class="ma-2" justify="center" align="center">
                <ClButton
                    class="logout-button"
                    color="primary"
                    @click="logoutButtonHandler()"
                    v-text="LogoutRoute.name"
                />
            </v-row>
        </template>
    </v-navigation-drawer>
</template>

<script>
// Vuex
import { mapGetters } from 'vuex';

// Routes
import LogoutRoute from '@/router/routes/Logout';
import SettingsRoute from '@/router/routes/Settings';
import MailSearchRoute from '@/router/routes/Mail/routes/Search';
import MailReceiveRoute from '@/router/routes/Mail/routes/Receive';
import MailShelveRoute from '@/router/routes/Mail/routes/Shelve';
import MailPickUpRoute from '@/router/routes/Mail/routes/PickUp';
import MailDeliveryRoute from '@/router/routes/Mail/routes/Delivery';
import InventoryShelveRoute from '@/router/routes/Inventory/routes/Shelve';
import InventoryPhysicalRoute from '@/router/routes/Inventory/routes/JoinPhysical';
import InventoryPartnerShipRoute from '@/router/routes/Inventory/routes/PartnerShip';

export default {
    name: 'TheNavbarDrawer',
    props: {
        value: {
            type: Boolean,
            required: true,
        },
        isOnline: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            linkGroupList: [
                {
                    title: 'Package Management',
                    routeList: [
                        MailSearchRoute,
                        MailReceiveRoute,
                        MailShelveRoute,
                        MailPickUpRoute,
                        MailDeliveryRoute,
                    ],
                },
                {
                    title: 'Inventory Management',
                    routeList: [
                        InventoryShelveRoute,
                        InventoryPhysicalRoute,
                        InventoryPartnerShipRoute,
                    ],
                },
                {
                    title: 'Settings',
                    routeList: [
                        SettingsRoute,
                    ],
                },
            ],

            LogoutRoute,
            selectedRouteKey: null,
        };
    },
    computed: {
        ...mapGetters( {
            rolesForCurrentLocationHasRole: 'User/rolesForCurrentLocationHasRole',
        } ),
        drawer: {
            get() {
                return this.value;
            },
            set( val ) {
                this.$emit( 'input', val );
            },
        },
        permissibleLinkGroupList() {
            return this.linkGroupList
                .map( ( linkGroup ) => ( {
                    ...linkGroup,
                    routeList: linkGroup.routeList.filter( ( route ) => (
                        !route.meta.allowedRoles
                            ? true
                            : route.meta.allowedRoles.some( this.rolesForCurrentLocationHasRole )
                    ) ),
                } ) )
                .filter( ( linkGroup ) => linkGroup.routeList.length > 0 );
        },
    },
    methods: {
        logoutButtonHandler() {
            this.$router.push( LogoutRoute );
        },
        /** @returns { Boolean } */
        isCurrentRoute( route ) {
            const isDirectRoute = route.name === this.$route.name;
            if ( !isDirectRoute ) {
                return route.children && this.currentRouteIsInList( route.children );
            }
            return isDirectRoute;
        },
        /** @returns { Boolean } */
        currentRouteIsInList( routeList ) {
            return routeList.findIndex( ( route ) => this.isCurrentRoute( route ) ) !== -1;
        },
    },
};
</script>

<style lang="scss" scoped>
.nav-heading {
    height: 72px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: $colors-primary;
    color:  $colors-primary;
    &__close-button {
        padding-right: 6px;
        text-align: right;
    }

    &__column {
        display: flex;
        align-items: center;
    }
}

#navigation-drawer .nav-list {
    width: 100%;

    &__nav-list-group .nav-list__nav-list-item {
        padding-left: 2rem;
        .v-list-item__title {
            font-weight: normal;
        }
    }

    &__nav-list-item,
    &__nav-list-group,
    .v-list-item__title,
    ::v-deep .v-list-item .v-icon {
        color: $colors-primary;
        font-weight: bold;
    }
    .v-list-item--link,
    ::v-deep .v-list-item {
        color: $colors-primary !important;
        font-weight: bold;
    }
}

.logout-button {
    margin: 1rem;
    width: 90%;
    max-width: 200px;
    margin-bottom: 15rem;
}
</style>
